import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
}

const SEOHead: React.FC<SEOHeadProps> = ({ 
  title = "Matthew Innes - Web Developer & Digital Designer Portfolio",
  description = "Professional portfolio of Matthew Innes, specialising in web development, digital design, and UX/UI solutions.",
  canonical
}) => {
  const location = useLocation();
  
  // Generate canonical URL based on current path
  const getCanonicalUrl = () => {
    const baseUrl = "https://matthewweb.com";
    if (canonical) {
      return canonical;
    }
    return `${baseUrl}${location.pathname}`;
  };

  useEffect(() => {
    // Update document title
    document.title = title;
    
    // Update or create canonical tag
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', getCanonicalUrl());
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);
    
    // Ensure robots meta tag allows indexing
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    
    // Update Open Graph URL
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', getCanonicalUrl());
    
    // Update Twitter URL
    let twitterUrl = document.querySelector('meta[name="twitter:url"]');
    if (!twitterUrl) {
      twitterUrl = document.createElement('meta');
      twitterUrl.setAttribute('name', 'twitter:url');
      document.head.appendChild(twitterUrl);
    }
    twitterUrl.setAttribute('content', getCanonicalUrl());
    
    // Add language and locale meta tags
    let langMeta = document.querySelector('meta[property="og:locale"]');
    if (!langMeta) {
      langMeta = document.createElement('meta');
      langMeta.setAttribute('property', 'og:locale');
      document.head.appendChild(langMeta);
    }
    langMeta.setAttribute('content', 'en_GB');
    
    // Add article:published_time for better indexing
    let publishedTime = document.querySelector('meta[property="article:published_time"]');
    if (!publishedTime) {
      publishedTime = document.createElement('meta');
      publishedTime.setAttribute('property', 'article:published_time');
      document.head.appendChild(publishedTime);
    }
    publishedTime.setAttribute('content', new Date().toISOString());
    
  }, [location.pathname, title, description, canonical]);

  return null; // This component doesn't render anything
};

export default SEOHead;
